import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { TrendingUp, Mail, Lock, AlertCircle, User, Crown, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Demo account credentials
const DEMO_ACCOUNTS = {
  free: { email: 'demo@virtuinvest.com', password: 'jan123', label: 'Free User', icon: User },
  pro: { email: 'admin@virtuinvest.com', password: 'jan123', label: 'Pro User', icon: Crown },
  admin: { email: 'admin@virtuinvest.com', password: 'jan123', label: 'Admin', icon: Shield }
};

// Quick test accounts - password is always "jan" for testing
const TEST_ACCOUNTS = {
  free: { email: 'free@fictoinvest.com', tier: 'free', label: 'Free Test' },
  pro: { email: 'pro@fictoinvest.com', tier: 'paid', label: 'Pro Test' },
  proplus: { email: 'proplus@fictoinvest.com', tier: 'pro', label: 'Pro+ Test' },
  admin: { email: 'admin@fictoinvest.com', tier: 'admin', label: 'Admin Test' }
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(null);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.requires2FA) {
        toast.info('2FA verification required');
        navigate('/2fa');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (accountType) => {
    const account = DEMO_ACCOUNTS[accountType];
    setError('');
    setDemoLoading(accountType);

    try {
      const result = await login(account.email, account.password);

      if (result.requires2FA) {
        toast.info('2FA verification required');
        navigate('/2fa');
      } else {
        toast.success(`Welcome! Logged in as ${account.label}`);
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Demo login failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setDemoLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
          </Link>
          <Link to="/register">
            <Button variant="outline" className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-green-50">
              Sign Up
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md space-y-8 animate-slide-up">
          {/* Login Card */}
          <Card className="border-stone-200 shadow-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-serif text-2xl text-center text-[#1F2937]">
                Welcome back
              </CardTitle>
              <CardDescription className="text-center text-gray-600">
                Enter your credentials to access your portfolio
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-white border-stone-200 focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-white border-stone-200 focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/10"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || demoLoading}
                  className="w-full bg-[#1A4D2E] hover:bg-[#143d24] text-white shadow-lg shadow-green-900/20 py-5"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Signing in...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              {/* Testing Quick Login */}
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-purple-600">Testing Mode</span>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mt-4">
                  {Object.entries(TEST_ACCOUNTS).map(([key, account]) => {
                    const tierColors = {
                      free: 'border-green-200 bg-green-50 hover:bg-green-100 text-green-700',
                      pro: 'border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700',
                      proplus: 'border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-700',
                      admin: 'border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700'
                    };
                    const tierLabels = {
                      free: 'Free',
                      pro: 'Pro',
                      proplus: 'Pro+',
                      admin: 'Admin'
                    };
                    return (
                      <Button
                        key={key}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEmail(account.email);
                          // Do NOT auto-fill password
                        }}
                        className={`flex flex-col items-center gap-1 py-2 h-auto border ${tierColors[key]}`}
                      >
                        <span className="text-xs font-bold uppercase">{tierLabels[key]}</span>
                        <span className="text-[10px]">Click</span>
                      </Button>
                    );
                  })}
                  </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link
                    to="/register"
                    className="font-medium text-[#1A4D2E] hover:underline"
                    data-testid="register-link"
                  >
                    Sign up free
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-stone-100 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500 mb-2">
            Educational simulator only. Not financial advice. No real trading.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/terms" className="text-sm text-gray-500 hover:text-[#1A4D2E] transition-colors">
              Terms & Conditions
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/privacy" className="text-sm text-gray-500 hover:text-[#1A4D2E] transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
